import {Counter, InfoTooltip} from '@wandb/components';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React from 'react';
import {useRouteMatch} from 'react-router-dom';

import {WBAgentRunCountTotalContextSlice} from '../../aria/externalBindings/WBAgentContextSlices';
import {GROUP_PATHS} from '../../routes/workspace/groupWorkspacePaths';
import {SWEEP_PATHS} from '../../routes/workspace/sweepWorkspacePaths';
import {maybePluralize} from '../../util/uihelpers';
import {
  useNumCrossProjectRunsCount,
  useProjectRunsCount,
  useSweepRunsCount,
} from './hooks/useRunSelectorCount';

const RunsCounterDisplay = ({
  numCurrProjectRuns,
  numCrossProjectRuns,
  isCountStale = false,
}: {
  numCurrProjectRuns: number;
  numCrossProjectRuns: number;
  // Only ever true on large projects (see useProjectRunsCount), where new runs
  // have likely been logged since load and we don't re-run the expensive
  // aggregation. When set, we replace the number with an info icon; otherwise
  // the count is shown.
  isCountStale?: boolean;
}) => {
  const totalCount = numCurrProjectRuns + numCrossProjectRuns;
  const hasCrossProjectRuns = numCrossProjectRuns > 0;
  return (
    <TailwindContents>
      <div className="flex items-center gap-3">
        {isCountStale ? (
          <InfoTooltip
            id="runs-counter-stale"
            aria-label="New runs logged"
            size="sm">
            New runs have been logged. This project is large, so the exact run
            count isn&apos;t shown here. Reload to see the current total.
          </InfoTooltip>
        ) : (
          <>
            <Counter
              value={totalCount}
              aria-label="runs"
              aria-describedby={
                hasCrossProjectRuns ? 'runs-counter-info' : undefined
              }
            />
            {hasCrossProjectRuns && (
              <InfoTooltip
                id="runs-counter-info"
                aria-label="Run count details"
                size="sm">
                Includes {maybePluralize(numCrossProjectRuns, 'run')} from other
                projects
              </InfoTooltip>
            )}
          </>
        )}
      </div>
    </TailwindContents>
  );
};

const ProjectRunsCounter = () => {
  const {count, loading, error, skipped, isCountStale} = useProjectRunsCount();
  const numCrossProjectRuns = useNumCrossProjectRunsCount();

  if (loading || error || skipped) {
    return null;
  }

  return (
    <>
      <WBAgentRunCountTotalContextSlice count={count} />
      <RunsCounterDisplay
        numCurrProjectRuns={count}
        numCrossProjectRuns={numCrossProjectRuns}
        isCountStale={isCountStale}
      />
    </>
  );
};

const SweepRunsCounter = () => {
  const {count, loading, error, skipped} = useSweepRunsCount();
  const numCrossProjectRuns = useNumCrossProjectRunsCount();

  if (loading || error || skipped) {
    return null;
  }

  return (
    <>
      <WBAgentRunCountTotalContextSlice count={count} />
      <RunsCounterDisplay
        numCurrProjectRuns={count}
        numCrossProjectRuns={numCrossProjectRuns}
      />
    </>
  );
};

export const RunsCounter = () => {
  const isSweepPage = useRouteMatch(SWEEP_PATHS) != null;
  const isGroupPage = useRouteMatch(GROUP_PATHS) != null;

  if (isGroupPage) {
    return null;
  }

  return isSweepPage ? <SweepRunsCounter /> : <ProjectRunsCounter />;
};
